/* global THREE, _, $, Rx, Redux, ReduxObservable, virtualDom, Promise, ProgressBar, console */

import {
	convertName,
	delegate,
	getFabs,
	getImage,
	getDimensions,
	checkWebglSupport,
	checkGyroSupport,
	getEnvironment,
	qp,
	getOptionsMatrixRegionId,
	getCulture,
	imgixRequest,
	conditionalValue
} from "./_utilities";
import {ACTIONS} from "./_actions";
import {store, store$} from "./_config-store";
import {
	Scene,
	PerspectiveCamera,
	WebGLRenderer,
	SphereGeometry,
	MeshBasicMaterial,
	Mesh,
	Math as ThreeMath,
	Raycaster,
	Texture,
	Vector2,
	Vector3,
} from "../libs/three.module";
import DeviceOrientationController from "./DeviceOrientationController";

// import this way so that it can be removed from production
// since webpack doesn't tree shake this out otherwise
const createLogger = (process.env.NODE_ENV !== 'production')
	? require("redux-logger").createLogger
	: store => next => action => next(action);
const {createStore, applyMiddleware, compose} = Redux;
const {createEpicMiddleware, combineEpics} = ReduxObservable;
const h = virtualDom.h;


/**
 * setup Interior Builder
 * @param selector
 * @param uiState
 * @returns {{}}
 */
export function setupInteriorBuilder(selector, uiState, store, rules) {
	let md = new MobileDetect(window.navigator.userAgent);
	let mobile = md.mobile() || md.tablet();
	let qparams = qp();
	let env = getEnvironment();
	const canvasWidth = (mobile && !qparams.devImages) ? 2048 : 4096,
		canvasHeight = (mobile && !qparams.devImages) ? 1024 : 2048;

	let interiorBuilder = {};
	let $container = $(selector);
	let container = $container[0];
	let interiorBuilderState = {
		uiState,
		container: $container,
		canvasWidth: (mobile && !qparams.devImages) ? 2048 : 4096,
		canvasHeight: (mobile && !qparams.devImages) ? 1024 : 2048,
		layers: [],
	};
	let firstUpdate = true;
	let imgFolder;
	let targetLat = 0, targetLon = 0;
	let camera;
	let prevCameraTarget = {
		x: 0,
		y: 0,
		z: 0
	};
	let scene, renderer;
	let raycaster, mouse = new Vector2(), INTERSECTED;
	let controls;
	let canvas, context;
	let layerMesh;
	let layerTexture, layerGeometry, layerMaterial;
	let onPointerDownPointerX = 0, onPointerDownPointerY = 0;
	let lon = 0, onPointerDownLon = 0;
	let lat = 0, onPointerDownLat = 0;
	let phi = 0, theta = 0;
	let radius = uiState.interiorViewRadius;
	let animFactor = 0.1;
	let animPrecision = 0.1;
	let moveFactor = mobile ? 0.5 : 0.1;
	let viewInitRotationDegrees = mobile ? 180 : -90;
	let jitterThreshold = 0.0005;
	let animateMovement = true;
	// let $progressBarMask = $('<div class="progress-bar-mask"><div class="progress-bar-wrap"></div></div>').appendTo($container);
	let $progressBarMask = $('<div class="progress-bar-mask"><div class="progress-bar-line"></div></div>').appendTo($container);
	let $progressBarLine = new ProgressBar.Line($progressBarMask.find(".progress-bar-line")[0], {
		strokeWidth: 4,
		easing: 'linear',
		duration: 100,
		color: '#5482ab',
		trailColor: '#e5e5e5',
		trailWidth: 4,
		svgStyle: {width: '100%', height: '100%'},
		text: {
			style: {
				// Text color.
				// Default: same as stroke color (options.color)
				color: '#5482ab',
				position: 'absolute',
				left: '50%',
				top: '5px',
				padding: 0,
				margin: 0,
				transform: 'translateX(-50%)'
			},
			autoStyleContainer: false
		},
		step: function (state, bar) {
			bar.setText(Math.round(bar.value() * 100) + ' %');
		}
	});
	let $360ViewUnavailable;
	let webgl = checkWebglSupport();
	let VTree, VNode;
	// should the viewer run;
	let run = false;
	let {alwaysOn, angleMap, interior: layerMap} = rules;

	interiorBuilder.el = $container;

	function setupBuilder(gyroSupported) {
		$360ViewUnavailable = $('<div class="interior-view-unavailable"></div>').appendTo($container);

		raycaster = new Raycaster();

		canvas = document.createElement("canvas");
		canvas.width = canvasWidth;
		canvas.height = canvasHeight;
		context = canvas.getContext("2d");
		context.globalCompositeOperation = "source-over";

		renderer = new WebGLRenderer();
		renderer.setPixelRatio(window.devicePixelRatio);
		renderer.setSize(container.clientWidth, container.clientHeight);
		renderer.domElement.style.position = 'absolute';
		renderer.domElement.style.top = 0;

		renderer.sortObjects = false;

		container.appendChild(renderer.domElement);

		scene = new Scene();

		camera = new PerspectiveCamera(75, container.clientWidth / container.clientHeight, 1, 1100);
		camera.target = new Vector3(0, 0, 0);

		layerTexture = new Texture(canvas);
		layerTexture.needsUpdate = true;
		layerGeometry = new SphereGeometry(500, 60, 40);
		layerGeometry.scale(-1, 1, 1);

		// mobile view is rotated 90 degrees in Y relative to desktop
		if (mobile) {
			layerGeometry.rotateY(Math.PI / 2);
		}

		layerMaterial = new MeshBasicMaterial({
			map: layerTexture,
			transparent: true
		});
		layerMesh = new Mesh(layerGeometry, layerMaterial);
		if (mobile) {
			layerMesh.rotation.y = ThreeMath.radToDeg(viewInitRotationDegrees);
		}
		scene.add(layerMesh);


		if (mobile && gyroSupported) {
			controls = new DeviceOrientationController(camera, renderer.domElement);
			controls.enableManualDrag = false;
			controls.connect();
		}
		animate();
	}

	function hideProgressBar() {
		$progressBarMask.hide();
		$progressBarMask.addClass("remainder");
		$progressBarLine.set(0);
	}

	/**
	 *
	 * @param i - initial value
	 * @param l - target value
	 * @param x - data to be passed back
	 * @param c - complete
	 * @param r - resie
	 * @param min - min value
	 * @param max - max value
	 * @returns {{imgData: *, progress: number, r: *}}
	 */
	function updateProgress(i, l, x, c, r, min = 0, max = 1) {
		var target = l > 0 ? l : 1,
			progress = min + ((i / target) * (max - min)),
			acc = {
				imgData: x,
				progress: progress,
				r
			};

		if (progress >= 0) {
			if (!r) $progressBarMask.show();
			// $progressBarLineWrap.show();
		}

		if (progress === 0) {
			$progressBarLine.set(0);
		} else {
			$progressBarLine.set($progressBarLine.value());
			$progressBarLine.animate(progress, () => {
				if (progress === 1) {
					hideProgressBar();
				}
			});
		}
		// console.log("update progress after: ", i, l, min, max, progress, c, r, $progressBarLine.value());
		return acc;
	}

	function interiorViewAvailable(state) {
		
		return _.includes(state.interiorViews, convertName(state.truckModel + "-" + state.truckCab));
	}

	// cache the results of getting the filenames
	let requestFilenames = _.memoize(function requestFilenames(path, layerName) {
		let region = getOptionsMatrixRegionId(),
			culture = getCulture();
		const root = env === 'DEV' ? 'https://freightlinertrucks-dev.azurewebsites.net' : '/umbraco/Api';
		const environment = env === 'DEV' ? '&env=local' : '';
		const url = `${root}/ConfiguratorImages?dir=${path}&region=${region}&culture=${culture}&image=${layerName}${environment}`;
		return $.ajax({
			url: url,
			method: 'GET',
			dataType: "json"
		});
	}, (dir, layerName) => {
		return dir + "/" + layerName;
	});

	function getFileName(dir, layerName) {
		var p = new Promise((resolve, reject) => {
			requestFilenames(dir, layerName).then(function ([{ path: filename, metaData } = { path: '', metaData:{}}]) {

				let baseMerge = filename
					? filename.split('/').slice(0, -1).join('/') + '/base_merge/' + (window.intBG ? window.intBG : filename.indexOf('day_cab') === -1 ? 'sleeper' : 'day_cab')
					: '';

				
				getDimensions({ path: filename.indexOf('-base.png') !== -1 ? `${baseMerge}-bg.png` : filename, metaData});
				resolve({ src: filename ?
					filename.indexOf('-base.png') !== -1 ? 
					imgixRequest({
						path: `${baseMerge}-bg.png`,
						options: {
							h: mobile ? 0.5 : undefined,
							w: mobile ? 0.5 : undefined,
							auto: ['format','compress'],
							fm: ['jp2','png8'],
							bm: 'normal',
							blend: {
								path: filename,
								options: {
									bm: 'normal',
									h: mobile ? 0.5 : undefined,
									w: mobile ? 0.5 : undefined,
									blend: window.intBG ? false : { path: `${baseMerge}-fg.png`, options: {
										h: mobile ? 0.5 : undefined,
										w: mobile ? 0.5 : undefined,
									}},
								}
							}
						}
					}) : 
					imgixRequest({
						path: filename,
						options: {
							h: mobile ? 0.5 : undefined,
							w: mobile ? 0.5 : undefined,
							auto: ['format','compress'],
							fm: ['jp2','png8'],
						}
					}) : '', metaData});
			}, function (error) {
				reject(error);
			});
		});
		return p;
	}

	function update() {
		var latDiff,
			lonDiff,
			coords;

		// calculate the current lat/lon given the target lat/lon and animation status
		if (!animateMovement) {
			lat = targetLat;
			lon = targetLon;
			animateMovement = true;
		} else {
			latDiff = absoluteDiff(lat, targetLat);
			lonDiff = absoluteDiff(lon, targetLon);

			if (latDiff > animPrecision) {
				if (lat > targetLat) {
					lat = lat - (latDiff * animFactor);
				} else {
					lat = lat + (latDiff * animFactor);
				}
			} else {
				targetLat = lat;
			}
			if (lonDiff > animPrecision) {
				if (lon > targetLon) {
					lon = lon - (lonDiff * animFactor);
				} else {
					lon = lon + (lonDiff * animFactor);
				}
			} else {
				targetLon = lon;
			}
		}

		//console.log('ANGLE',[lat,lon]);

		// convert lat/lon to x,y,z coords to point the camera there
		coords = getCartesianCoordinatesOnSphereFromLonLat(lat, lon, radius);
		// camera.target.set(coords.x, coords.y, coords.z);
		// camera.lookAt(camera.target);
		if (!mobile || !controls) {
			layerMesh.rotation.x = -ThreeMath.degToRad(lat);
		}
		layerMesh.rotation.y = ThreeMath.degToRad(lon + viewInitRotationDegrees);

		if (typeof Stats === "function") {
			stats.update();
		}


		// update view based on deviceorientation
		if (controls) {
			controls.update();
		}

		let v = new Vector3(0, 0, -1);
		v.applyQuaternion(camera.quaternion);


		prevCameraTarget.x = v.x;
		prevCameraTarget.y = v.y;
		prevCameraTarget.z = v.z;


		renderer.render(scene, camera);
	}

	/**
	 * get cartesian coordinates from lat and lon and radius
	 * @param lon -180 to 180
	 * @param lat -90 to 90
	 * @param radius
	 * @returns {{x: number, y: number, z: number}}
	 */
	function getCartesianCoordinatesOnSphereFromLonLat(lat, lon, radius) {
		let latitude = Math.max(-90, Math.min(90, lat)),
			phi = ThreeMath.degToRad(90 - latitude),
			theta = ThreeMath.degToRad(lon + 180),
			r = radius || 500;
		return {
			x: r * Math.sin(phi) * Math.cos(theta),
			y: r * Math.cos(phi),
			z: r * Math.sin(phi) * Math.sin(theta)
		};
	}

	/**
	 * get latitude and longitude from cartesian coordinates
	 * @param x
	 * @param y
	 * @param z
	 * @returns {{lat: number, lon: number}}
	 */
	function getLatLonFromCartesianCoordinates(x, y, z, radius) {
		return {
			lat: ThreeMath.radToDeg(Math.asin(z / radius)),
			lon: ThreeMath.radToDeg(Math.atan2(y, x))
		}
	}

	function animate() {
		window.requestAnimationFrame(animate);
		if (run) {
			update();
		}

	}

	function absoluteDiff(a, b) {
		return Math.abs(b - a);
	}

	function convert3DWorldTo2DScreen(obj, camera, renderer) {
		var vector = new Vector3(obj.position.x, obj.position.y, obj.position.z);
		var canvas = renderer.domElement;

		// map to normalized device coordinate (NDC) space
		vector.project(camera);

		// map to 2D screen space
		vector.x = Math.round(( vector.x + 1 ) * parseInt(canvas.style.width) / 2);
		vector.y = Math.round(( -vector.y + 1 ) * parseInt(canvas.style.height) / 2);
		vector.z = 0;
		return vector;
	}

	function normalizeGestureData(e) {
		let touches = e.event.originalEvent.touches;
		return {
			touches,
			multitouch: touches && touches.length > 1,
			x: (touches ? touches[0].clientX : e.event.clientX) - $container.offset().left,
			y: (touches ? touches[0].clientY : e.event.clientY) - $("#configurator-viewers").position().top
		};
	}

	let mouseout$ = delegate($container, selector, 'mouseout');

	let gestureStart$ = delegate($container, selector, "mousedown touchstart")
		.map(normalizeGestureData);
	let gestureMove$ = delegate($container, selector, "mousemove touchmove")
		.map(normalizeGestureData);
	let gestureEnd$ = delegate($container, selector, "mouseup touchend");
	let optionsInViewChange$ = store$
		.filter(state => state.actionType === ACTIONS.OPTIONS_IN_VIEW_CHANGE)
		.filter(state => {
			return _.every(state.optionsInView, "attributes");
		});


	let gesture$ = gestureStart$
		.flatMap(start => {
			// console.log("gesture start", start);
			onPointerDownPointerX = start.x;
			onPointerDownPointerY = start.y;

			onPointerDownLon = lon;
			onPointerDownLat = lat;

			mouse.x = ( start.x / container.clientWidth ) * 2 - 1;
			mouse.y = -( start.y / container.clientHeight ) * 2 + 1;

			return gestureMove$.map(t => {
				// console.log("gesture move", t);
				return t;
			}).takeUntil(Rx.Observable.merge(gestureEnd$, mouseout$));
		});
	gesture$
		.subscribe(t => {
			if (!t.multitouch) {
				targetLon = ( onPointerDownPointerX - t.x ) * moveFactor + onPointerDownLon;
				targetLat = Math.max(-90, Math.min(90, ( t.y - onPointerDownPointerY ) * moveFactor + onPointerDownLat));
				mouse.x = ( t.x / container.clientWidth ) * 2 - 1;
				mouse.y = -( t.y / container.clientHeight ) * 2 + 1;
			}
		});

	let mousewheel$ = delegate($container, selector, "mousewheel MozMousePixelScroll");
	mousewheel$.subscribe(e => {
		let event = e.event.originalEvent;
		// WebKit
		if (event.wheelDeltaY) {
			camera.fov -= event.wheelDeltaY * 0.05;

			// Opera / Explorer 9
		} else if (event.wheelDelta) {
			camera.fov -= event.wheelDelta * 0.05;

			// Firefox
		} else if (event.detail) {
			camera.fov += event.detail * 1.0;
		}
		// camera fov min = 35
		// camera fov max = 90
		camera.fov = Math.max(35, Math.min(90, camera.fov));
		camera.updateProjectionMatrix();
	});

	let resize$ = Rx.Observable.fromEvent(window, "resize")
		.filter(() => {
			return document.querySelector(".vdom.viewers").dataset.viewer === "interior";
		})
		.subscribe(updateRendererSize);

	let startTeaserRotation = () => {
		Rx.Observable.interval(30)
			.takeUntil(Rx.Observable.merge(gestureStart$, optionsInViewChange$))
			.subscribe(() => {
				// console.log("auto rotating", onPointerDownPointerX, onPointerDownLon, lon);
				onPointerDownPointerX += 10;
				onPointerDownLon = lon;
				targetLon = 10 * moveFactor + onPointerDownLon;
			});
	};
	if (!mobile) {
		startTeaserRotation();
	}

	/**
	 * draw image layers to canvas, update the webgl texture
	 * @param layers
	 */
	function drawImageLayers(layers) {
		context.fillStyle = "#444444";
		context.fillRect(0, 0, canvasWidth, canvasHeight);
		let promise = new Promise((resolve, reject) => {
			Rx.Observable.from(layers)
				.concatMap(layer => {
					return Rx.Observable.fromPromise(getImage(layer.src,undefined,true));
				})
				.filter((img, i) => {
					// update progress bar
					updateProgress(i, layers.length - 1, img, false, false);
					return img;
				})
				/**
				 * because imgix resizes the images for mobile
				 * we need to recompute the dimensions of the images given by the filename
				 */
				.map(imgData => {
					
					if (mobile && !qparams.devImages) {
						imgData.dims.width = imgData.dims.width / 2;
						imgData.dims.height = imgData.dims.height / 2;
						imgData.dims.x = imgData.dims.x / 2;
						imgData.dims.y = imgData.dims.y / 2;
						return imgData;
					} else {
						return imgData;
					}
				})
				.subscribe(imgData => {
					context.drawImage(imgData.img, 0, 0,
						imgData.dims.width, imgData.dims.height,
						imgData.dims.x, imgData.dims.y,
						imgData.dims.width, imgData.dims.height);
					}, e => {
						console.error('subscribe error', e);
					},
					() => {
						layerTexture.needsUpdate = true;
						resolve();
					});
		});
		return promise;
	}

	/**
	 * method to center the view
	 */
	interiorBuilder.centerView = () => {
		if (controls) {
			//controls.reset();
		} else {
			targetLat = 0;
			targetLon = 0;
		}
	};

	function getLayerFile(uiState, layer) {
//		const regionDir = getRegionDir();
// REFACTOR TODO: 
// interior/${convertName(uiState.truckModel)}
		let imgFolder = `interior/${convertName(uiState.truckModel)}`;
		return Rx.Observable.fromPromise(getFileName(imgFolder, layer))
			.retry(3)
			.catch(e => {
				console.error(`Error getting filename for ${imgFolder}, ${layer}`);
				return Rx.Observable.of("");
			});
	}

	/**
	 * repaint the layers given the state of the app
	 * @param uiState - main state object
	 */
	function render(state) {
		let uiState = state.uiState;
		run = (state.uiState.view === "interior" && webgl && interiorViewAvailable(state.uiState));

		if (!run) {
			$360ViewUnavailable.addClass("active");
			return;
		} else {
			$360ViewUnavailable.removeClass("active");
		}
		radius = uiState.interiorViewRadius;
		
		Rx.Observable.from(state.layers).concatMap((layer) => {
			return getLayerFile(uiState, layer);
		}).bufferCount(state.layers.length)
			.filter(filenames => {
				return filenames.length > 0;
			})
			.subscribe(l => {
				let layers = _.compact(_.flatten(l));
				Rx.Observable.fromPromise(drawImageLayers(layers, true)).subscribe(() => {
					firstUpdate = false;
					updateRendererSize();
				});
			});
	};

	if (typeof Stats === "function") {
		var stats = new Stats();
		stats.domElement.style.position = 'absolute';
		stats.domElement.style.top = '0px';
		stats.domElement.style.zIndex = 100;
		container.appendChild(stats.domElement);
	}

	function updateRendererSize() {
		if (!camera) {
			return;
		}
		let configuratorViewers = document.querySelector("#configurator-viewers");
		let width = configuratorViewers.clientWidth;
		let height = configuratorViewers.clientHeight;
		camera.aspect = width / height;
		camera.updateProjectionMatrix();
		renderer.setSize(width, height);
	}

	function mapLayerArrayWithReuseMap(state, layerArray) {
		return _.chain(layerArray).compact().map(layer => {
			// map the layers to existing (reused) image in the map file
			if (state.interiorMap[layer]) {
				/*
					remove extensions from name found in map so that blob search
					on server can find it
				*/
				return state.interiorMap[layer].replace(/\.\w*$/, "");
			}
			return layer.replace(/\.\w*$/, "");
		}).value();
	}

	function getLayer({state, option, template}) {
		let truckModelCab = convertName(state.truckModel + "-" + state.truckCab),
			renderedValue = conditionalValue({state,standard:state.interiorBuildSettings[option],conditions:{'*':['temp',template]}});

		return renderedValue ? 
			`${truckModelCab}-${renderedValue}.png` :
			false;

		// switch (option) {
		// 	case "Mirrors": {
		// 		return truckModelCab + "-int_mirrors-mirrors_hood.png"
		// 	}
		// 	case "Base": {
		// 		return truckModelCab + "-base.png"
		// 	}
		// 	case "Trim Color": {
		// 		return state.interiorBuildSettings[option]
		// 			? truckModelCab + "-base_color-" + state.interiorBuildSettings[option] + ".png"
		// 			: false;
		// 	}
		// 	case "Elite Trim": {
		// 		return state.interiorBuildSettings[option]
		// 			? truckModelCab + "-base_trim-" + state.interiorBuildSettings[option] + ".png"
		// 			: false;
		// 	}
		// 	case "Upper Sleeper": {
		// 		return state.interiorBuildSettings[option]
		// 			? truckModelCab + "-upper-" + state.interiorBuildSettings[option] + ".png"
		// 			: false;
		// 	}
		// 	case "Lower Sleeper Lounge": {
		// 		return state.interiorBuildSettings[option] && state.interiorBuildSettings["Trim Color"] && state.interiorBuildSettings["Elite Trim"]
		// 			? truckModelCab + "-lower-" + state.interiorBuildSettings[option] + "-" + state.interiorBuildSettings["Trim Color"] + "-" + state.interiorBuildSettings["Elite Trim"] + ".png"
		// 			: false;
		// 	}
		// 	case "Refrigerator": {
		// 		return state.interiorBuildSettings[option]
		// 			? truckModelCab + "-cabinet-" + state.interiorBuildSettings[option] + ".png"
		// 			: false;
		// 	}
		// 	case "Seat Upholstery": {
		// 		return state.interiorBuildSettings[option] && state.interiorBuildSettings["Elite Trim"] && state.interiorBuildSettings["Trim Color"]
		// 			? truckModelCab + "-seats-" + state.interiorBuildSettings[option] + "-" + state.interiorBuildSettings["Elite Trim"] + "-" + state.interiorBuildSettings["Trim Color"] + ".png"
		// 			: false;
		// 	}
		// 	case "CB": {
		// 		return truckModelCab + "-cb-cb_on.png"
		// 	}
		// 	case "Shifter": {
		// 		return state.interiorBuildSettings[option]
		// 			? truckModelCab + "-shifter-" + state.interiorBuildSettings[option] + ".png"
		// 			: false;
		// 	}
		// 	case "SOD": {
		// 		return _.values(state.miscBuildSettings).indexOf('sod_bendix') !== -1
		// 			? truckModelCab + "-sod-" + 'sod_bendix' + ".png"
		// 			: false;
		// 	}
		// 	default: {
		// 		return ""
		// 	}
		// }
	}

	function getLayers(state) {
		// create the layers, order and layer types are hard-coded in layer array
		let layerArray = Object.keys(layerMap).map(option => getLayer({state, option, template: layerMap[option]}));
		let mappedLayerArray = mapLayerArrayWithReuseMap(state, layerArray);

		// console.log("pre map:", layerArray);
		// console.log("post map:", mappedLayerArray);

		return mappedLayerArray;
	}

	function intents() {
		let uiEvent$ = store$
			.filter(state => state.dataReady && state.view === "interior");
		let uiUpdate$ = uiEvent$
			.filter(state => {
				return state.truckModel !== "" && state.truckCab !== "" &&
					state.actionType !== ACTIONS.PILLAR_CHANGE &&
					state.actionType !== ACTIONS.EXTERIOR_BUILD_CHANGE &&
					state.actionType !== ACTIONS.POWERTRAIN_BUILD_CHANGE &&
					state.actionType !== ACTIONS.SAFETY_BUILD_CHANGE
			})
			.map(state => ({
				type: ACTIONS.UI_UPDATE,
				data: {
					state
				}
			}));
		let cabChange$ = uiEvent$
			.filter(state => state.actionType === ACTIONS.CAB_CHANGE)
			.map(state => ({
				type: ACTIONS.CAB_CHANGE,
				data: {
					state
				}
			}));

		let optionsInViewChange$ = uiEvent$
			.filter(state => state.actionType === ACTIONS.OPTIONS_IN_VIEW_CHANGE
				|| state.actionType === ACTIONS.VIEW_OPTION_CHANGE);

		let rotateInterior$ = optionsInViewChange$
			.filter(state => {
				return _.every(state.optionsInView, "attributes")
					|| state.optionsInView[0].group === "Materials";
			})
			.map(state => ({
				type: ACTIONS.ROTATE_INTERIOR,
				data: state.activeOption
			}));

		let resizeInterior$ = optionsInViewChange$
			.delay(400)
			.map(state => ({
				type: ACTIONS.RESIZE_INTERIOR,
				data: state.activeOption
			}));

		let preCacheActiveOption$ = optionsInViewChange$
			.filter(state => {
				return _.every(state.optionsInView, "attributes");
			})
			.map(state => ({
				type: ACTIONS.PRECACHE_ACTIVE_OPTION,
				data: state.optionsInView
			}));


		return Rx.Observable.merge(
			uiUpdate$,
			cabChange$,
			rotateInterior$,
			resizeInterior$,
			preCacheActiveOption$
		);
	}

	function interiorBuilderReducers(state = interiorBuilderState, action) {
		switch (action.type) {
			case ACTIONS.UI_UPDATE: {
				return Object.assign({}, state, {
					uiState: action.data.state,
					layers: getLayers(action.data.state)
				});
			}
			default: {
				return state;
			}
		}
	}

	const interiorCabChangeEpic$ = (action$, store) => action$.ofType(ACTIONS.CAB_CHANGE)
		.map(action => {
			// console.log("clear rotation");
			interiorBuilder.centerView();
			return action.data.state;
		})
		.ignoreElements();

	const rotateInteriorToOption$ = (action$, store) => action$.ofType(ACTIONS.ROTATE_INTERIOR)
		.map(action => angleMap[action.data])
		.filter(rotation => rotation)
		.do(rotation => {
			targetLat = rotation[0];
			targetLon = rotation[1];
		})
		.ignoreElements();

	const resizeInterior$ = (action$, store) => action$.ofType(ACTIONS.RESIZE_INTERIOR)
		.do(() => {
			updateRendererSize();
		})
		.ignoreElements();

	const interiorBuilderEpics = combineEpics(
		interiorCabChangeEpic$,
		rotateInteriorToOption$,
		resizeInterior$
	);
	const interiorBuilderEpicMiddleware = createEpicMiddleware(interiorBuilderEpics);

	const middleware = [interiorBuilderEpicMiddleware];
	if (process.env.NODE_ENV !== `production`) {
		middleware.push(createLogger({
			collapsed: true
		}));
	}

	interiorBuilder.store = createStore(interiorBuilderReducers,
		compose(
			applyMiddleware(
				...middleware
			),
			window.devToolsExtension && process.env.NODE_ENV === `development` ? window.devToolsExtension() : (f => f)
		));
	interiorBuilder.store$ = Rx.Observable.from(interiorBuilder.store);

	intents(interiorBuilder.el).subscribe(action => {
		interiorBuilder.store.dispatch(action)
	});

	function renderPipeline() {
		// init the render process
		VTree = render(interiorBuilderState);
		VNode = virtualDom.create(VTree);
		// interiorBuilder.el.html(VNode);

		interiorBuilder.store$
			.filter(state => state.uiState.dataReady)
			.distinctUntilChanged((prevState, currState) => {
				return _.isEqual(prevState.layers, currState.layers);
			})
			.map(render)
			.startWith(VTree)
			.pairwise()
			.subscribe(([a, b]) => {
				// let patches = virtualDom.diff(a, b);
				// let state = interiorBuilder.store.getState();
				// VNode = virtualDom.patch(VNode, patches);
				// //interiorBuilder.paintSubject.next(state);
			});
	}

	checkGyroSupport().then(gyroSupport => {
		setupBuilder(gyroSupport);
	}).then(renderPipeline);


	return interiorBuilder;
}
